import { validPreviewUrl } from './previewUrl';

const CMS_URL = import.meta.env.VITE_BPAS_CMS_URL;

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    return value.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return [];
};

export async function fetchCmsData({ timeoutMs = 5000 } = {}) {
  if (!CMS_URL) {
    throw new Error('CMS_URL_MISSING');
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    const url = new URL(CMS_URL);
    url.searchParams.set('refresh', 'true');
    url.searchParams.set('nocache', 'true');
    url.searchParams.set('bust', Date.now().toString());
    url.searchParams.set('clientTs', Date.now().toString());

    const response = await fetch(url.toString(), {
      method: 'GET',
      cache: 'no-store',
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const json = await response.json();

    if (!json.ok) {
      throw new Error(json.message || 'System returned ok: false');
    }

    const normalizeWebsiteRef = (row) => {
      const imageUrl = row.imageUrl || row.ImageURL || '';
      const heroImageUrl = row.heroImageUrl || row.HeroImageURL || '';
      const resolvedImageUrl = row.resolvedImageUrl || '';

      return {
        active: typeof row.active === 'string' ? row.active.toLowerCase() === 'true' : row.active ?? true,
        featured: typeof row.featured === 'string' ? row.featured.toLowerCase() === 'true' : row.featured ?? false,
        featuredRank: row.featuredRank ?? 99,
        displayPriority: row.displayPriority ?? 100,
        websiteId: row.websiteId || row.WebsiteID || `ref-${(row.publicTitle || row.websiteName || 'reference').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
        websiteName: row.websiteName || '',
        publicTitle: row.publicTitle || 'Untitled Reference',
        businessType: row.businessType || 'General',
        primaryCategory: row.primaryCategory || 'General',
        tags: toArray(row.tags),
        outcomeType: row.outcomeType || 'Lead Generation',
        shortDescription: row.shortDescription || '',
        longDescription: row.longDescription || '',
        location: row.location || '',
        state: row.state || '',
        region: row.region || '',
        suburbs: toArray(row.suburbs),
        imageUrl,
        heroImageUrl,
        resolvedImageUrl,
        websiteUrl: validPreviewUrl(row.websiteUrl || row.WebsiteURL || ''),
        previewUrl: validPreviewUrl(row.previewUrl || row.PreviewURL || ''),
        buttonText: row.buttonText || 'View Page',
        searchKeywords: toArray(row.searchKeywords),
        seoTitle: row.seoTitle || '',
        seoDescription: row.seoDescription || '',
        aeoQuestion: row.aeoQuestion || '',
        aeoAnswer: row.aeoAnswer || '',
      };
    };

    const normalizeSeoLocation = (row) => ({
      active: row.active ?? true,
      sortOrder: row.sortOrder ?? 100,
      state: row.state || '',
      displayGroup: row.displayGroup || '',
      region: row.region || '',
      suburbs: toArray(row.suburbs),
      suburb: row.suburb || '',
      bpasPageTitle: row.bpasPageTitle || '',
      bpasMetaDescription: row.bpasMetaDescription || '',
      bpasH1: row.bpasH1 || '',
      bpasIntroCopy: row.bpasIntroCopy || row.introCopy || '',
      bpasAeoQuestion: row.bpasAeoQuestion || row.aeoQuestion || '',
      bpasAeoAnswer: row.bpasAeoAnswer || row.aeoAnswer || '',
      bpasServiceKeywords: toArray(row.bpasServiceKeywords),
      bpasTargetIndustries: toArray(row.bpasTargetIndustries),
      bpasInternalLinkLabel: row.bpasInternalLinkLabel || '',
      bpasInternalLinkUrl: row.bpasInternalLinkUrl || '',
      bpasLastUpdated: row.bpasLastUpdated || '',
      bpasInternalNotes: row.bpasInternalNotes || '',
      introCopy: row.introCopy || row.bpasIntroCopy || '',
      aeoQuestion: row.aeoQuestion || row.bpasAeoQuestion || '',
      aeoAnswer: row.aeoAnswer || row.bpasAeoAnswer || '',
    });

    const normalizeFooterLink = (row) => ({
      active: row.active ?? true,
      sortOrder: row.sortOrder ?? 100,
      category: row.category || 'Links',
      title: row.title || '',
      description: row.description || '',
      url: row.url || '#',
      iconType: row.iconType || 'Link',
      openMode: row.openMode || '_self',
      lastUpdated: row.lastUpdated || '',
    });

    return {
      settings: json.settings || {},
      websiteReferences: (json.websiteReferences || []).map(normalizeWebsiteRef),
      seoLocations: (json.seoLocations || []).map(normalizeSeoLocation),
      footerLinks: (json.footerLinks || []).map(normalizeFooterLink),
    };
  } catch (error) {
    throw error;
  }
}